import { Action, State, StateContext, Store } from '@ngxs/store';
import { carriesAValue } from '../../../utils/an-answer-that-carries-its-value';
import { inject, Injectable } from '@angular/core';
import {
  GetChatAction,
  GetChatLoadedAction,
  GetChatsAction,
  HandleNotReadMessagesAction,
  JoinChatChannelAction,
  LeaveChatChannelAction,
  LoadMoreMessagesAction,
  NewMessageReceivedAction,
  ReactToMessageAction,
  SendMessageTextAction,
  SetLoadingChatAction,
  SyncArchivedChatViewAction,
} from './messaging.action';
import { Channel } from '@smatchy/chat-client';
import { ChatListItem, ChatsService, ChatWithUsers, MessageViewDto } from '@smatchy/sdk';
import { applyMessageReaction, MessageReactedEvent, NewMessageSent } from '@smatchy/chat-domain';
import { UserProfileSelector } from '@smatchy/core';
import { catchError, of, Subscription, tap } from 'rxjs';
import { ShowToastErrorAction } from '../../app';
import { TranslateService } from '@ngx-translate/core';

export interface MessagingStateModel {
  isLoading: boolean;
  currentChatId: string;
  chats: ChatListItem[];
  archivedChats: ChatListItem[];
  /** Absent until a chat client connects, which is what the defaults below set. */
  channel?: Channel;
  messages: MessageViewDto[];
  /** Absent until a conversation is opened, which is what the defaults below set. */
  currentChat?: ChatWithUsers;
  fromNbMessage: number;
  canLoadMore: boolean;
  messageSubscription: Subscription | null;
  reactionSubscription: Subscription | null;
  currentChatError: string | null;
}

const defaults: MessagingStateModel = {
  isLoading: false,
  currentChatId: '',
  chats: [],
  archivedChats: [],
  channel: undefined,
  messages: [],
  currentChat: undefined,
  fromNbMessage: 0,
  canLoadMore: true,
  messageSubscription: null,
  reactionSubscription: null,
  currentChatError: null,
};

function mergeMessages(current: MessageViewDto[], incoming: MessageViewDto[]): MessageViewDto[] {
  const map = new Map<string, MessageViewDto>();
  (current || []).forEach((msg) => {
    if (msg && msg._id) map.set(msg._id, msg as MessageViewDto);
  });
  (incoming || []).forEach((msg) => {
    if (msg && msg._id) map.set(msg._id, msg as MessageViewDto);
  });
  return Array.from(map.values());
}

function withReactions(
  messages: MessageViewDto[],
  messageId: string,
  reactions: { userId: string; emoji: string }[],
): MessageViewDto[] {
  return messages.map((message) => (message._id === messageId ? { ...message, reactions } : message));
}

@State<MessagingStateModel>({
  name: 'messaging',
  defaults,
})
@Injectable()
export class MessagingState {
  private readonly _chatsService = inject(ChatsService);
  private readonly _store = inject(Store);
  private readonly _translate = inject(TranslateService);

  @Action(JoinChatChannelAction)
  async joinChatChannelAction(
    { patchState, getState, dispatch }: StateContext<MessagingStateModel>,
    { channel }: JoinChatChannelAction,
  ) {
    patchState({
      channel,
      messageSubscription: channel.message().subscribe((message: NewMessageSent) => {
        const currentChat = getState().currentChat;
        if (currentChat && !currentChat.isChatActive) {
          return;
        }
        patchState({
          messages: mergeMessages(getState().messages, [message]),
        });

        dispatch(new NewMessageReceivedAction());
      }),
      // SMA-22: the reactions of everyone in the conversation, live. What the
      // server sends is the truth, and replaces whatever was shown.
      reactionSubscription: channel.reactions().subscribe((event: MessageReactedEvent) => {
        patchState({
          messages: withReactions(getState().messages, event.messageId, event.reactions),
        });
      }),
    });
  }

  @Action(LeaveChatChannelAction)
  async leaveChatChannelAction({ patchState, getState }: StateContext<MessagingStateModel>) {
    const { messageSubscription, reactionSubscription } = getState();

    if (messageSubscription) {
      messageSubscription.unsubscribe();
    }
    if (reactionSubscription) {
      reactionSubscription.unsubscribe();
    }
    const { channel } = getState();
    if (channel) {
      channel.leaveChannel();
    }

    patchState({
      channel: undefined,
      messageSubscription: null,
      reactionSubscription: null,
    });
  }

  @Action(SetLoadingChatAction)
  setLoadingChatAction({ patchState }: StateContext<MessagingStateModel>) {
    patchState({
      isLoading: true,
      currentChatError: null,
    });
  }

  @Action(SendMessageTextAction)
  async sendMessageTextAction({ getState }: StateContext<MessagingStateModel>, { text }: SendMessageTextAction) {
    // No channel, no message: the chat client connects after the screen opens,
    // and the state says so since the defaults set it to nothing.
    getState().channel?.sendMessage(text);
  }

  @Action(ReactToMessageAction)
  reactToMessageAction(
    { patchState, getState }: StateContext<MessagingStateModel>,
    { messageId, emoji }: ReactToMessageAction,
  ) {
    const { channel, messages, currentChat } = getState();
    const myUserId = this._store.selectSnapshot(UserProfileSelector.userId);
    if (!channel || !myUserId || (currentChat && !currentChat.isChatActive)) {
      return;
    }
    const message = messages.find((candidate) => candidate._id === messageId);
    if (!message) {
      return;
    }
    // Shown at once; the server's answer, a moment later, settles it.
    patchState({
      messages: withReactions(messages, messageId, applyMessageReaction(message.reactions, myUserId, emoji)),
    });
    channel.react(messageId, emoji);
  }

  @Action(GetChatsAction)
  async getChatsAction({ patchState }: StateContext<MessagingStateModel>, { archived }: GetChatsAction) {
    return this._chatsService
      .getMyChats({
        archived: archived ? 'true' : 'false',
      })
      .pipe(
        tap((res) => {
          if (!carriesAValue(res)) {
            // The server's own error text used to go straight into a system
            // popup, untranslated and written for us rather than for the person.
            this._store.dispatch(new ShowToastErrorAction(this._translate.instant('GENERICS.ERROR')));
          } else {
            patchState(archived ? { archivedChats: res.value.chats } : { chats: res.value.chats });
          }
        }),
      );
  }

  @Action(SyncArchivedChatViewAction)
  async syncArchivedChatViewAction(
    _context: StateContext<MessagingStateModel>,
    { chatId }: SyncArchivedChatViewAction,
  ) {
    return this._chatsService.syncArchivedChatView({ id: chatId }).pipe(
      catchError((err) => {
        console.error('[SyncArchivedChatViewError]', err);
        return of(null);
      }),
    );
  }

  @Action(LoadMoreMessagesAction)
  async loadMoreMessagesAction(
    { patchState, getState }: StateContext<MessagingStateModel>,
    { count, chatId }: LoadMoreMessagesAction,
  ) {
    if (!getState().canLoadMore) {
      return;
    }
    const id = chatId ?? getState().channel?.id ?? getState().currentChatId;
    if (!id) {
      console.error('No chatId available for loadMoreMessagesAction');
      return;
    }

    return this._chatsService
      .loadChatMessages({
        id,
        loadChatMessagesDto: {
          from: getState().fromNbMessage,
          count,
        },
      })
      .pipe(
        tap((res) => {
          if (!carriesAValue(res)) {
            console.error('[LoadChatMessagesError]', res.error);
            return;
          }

          const loadedMessages = res.value.messages;
          patchState({
            messages: this.mergeMessages(getState().messages, loadedMessages),
            fromNbMessage: res.value.cursorPosition,
            canLoadMore: res.value.haveNext,
          });
        }),
        catchError((err) => {
          console.error('[LoadChatMessagesHttpError]', err);
          patchState({ canLoadMore: false });
          return of(null);
        }),
      );
  }

  @Action(GetChatAction)
  async getChatAction(
    { patchState, getState, dispatch }: StateContext<MessagingStateModel>,
    { chatId }: GetChatAction,
  ) {
    patchState({
      isLoading: true,
      currentChatError: null,
      currentChatId: chatId,
    });
    return this._chatsService
      .getChatById({
        id: chatId,
      })
      .pipe(
        tap((response) => {
          if (!carriesAValue(response)) {
            this._store.dispatch(new ShowToastErrorAction(this._translate.instant('GENERICS.ERROR')));
            patchState({
              isLoading: false,
            });
            return;
          }
          patchState({
            currentChat: response.value.chat,
            currentChatId: chatId,
            messages: response.value.messages,
            fromNbMessage: response.value.messages.length,
            isLoading: false,
            canLoadMore: response.value.messages.length >= 50,
          });

          dispatch(new GetChatLoadedAction());
        }),
        catchError((error) => {
          console.error(error);
          patchState({
            isLoading: false,
            currentChatError: error.message,
          });
          return of(error);
        }),
      );
  }

  @Action(HandleNotReadMessagesAction)
  handleNotReadMessagesAction(
    { patchState, getState }: StateContext<MessagingStateModel>,
    { messageIds }: HandleNotReadMessagesAction,
  ) {
    const messageOfCurrentChat = getState().messages.filter((message) => messageIds.includes(message._id));

    if (!messageOfCurrentChat.length) {
      return;
    }

    const userId = this._store.selectSnapshot(UserProfileSelector.userId);
    if (!userId) {
      // Without our own id, no message can be told apart as read by us, and
      // marking them read would name nobody. The snapshot is empty until the
      // profile has loaded.
      return;
    }

    const msgIdsNotRead = messageOfCurrentChat
      .filter((msg) => !msg.readByUserIds.includes(userId))
      .map((msg) => msg._id);

    if (msgIdsNotRead.length === 0) {
      return;
    }

    return this._chatsService
      .readMessages({
        readMultiMessageOfChatDto: {
          messageIds: msgIdsNotRead,
        },
      })
      .pipe(
        tap((res) => {
          if (res.error) {
            // The server's own error text used to go straight into a system
            // popup, untranslated and written for us rather than for the person.
            this._store.dispatch(new ShowToastErrorAction(this._translate.instant('GENERICS.ERROR')));
            throw new Error(String(res.error));
          }

          patchState(this.markMessagesAsRead(getState(), userId, msgIdsNotRead));
        }),
      );
  }

  private mergeMessages(messages: MessageViewDto[], newMessages: MessageViewDto[]): MessageViewDto[] {
    const messagesById = new Map<string, MessageViewDto>();

    [...messages, ...newMessages].forEach((message) => {
      messagesById.set(message._id, message);
    });

    return [...messagesById.values()];
  }

  private markMessagesAsRead(
    state: MessagingStateModel,
    userId: string,
    messageIds: string[],
  ): Partial<MessagingStateModel> {
    const messageIdsMarkedAsRead = new Set(messageIds);
    const readMessages = state.messages.filter(
      (message) => messageIdsMarkedAsRead.has(message._id) && !message.readByUserIds.includes(userId),
    );
    const readMessagesByChatId = readMessages.reduce<Record<string, number>>((acc, message) => {
      acc[message.chatId] = (acc[message.chatId] ?? 0) + 1;
      return acc;
    }, {});

    return {
      messages: state.messages.map((message) => {
        if (!messageIdsMarkedAsRead.has(message._id) || message.readByUserIds.includes(userId)) {
          return message;
        }

        return {
          ...message,
          readByUserIds: [...message.readByUserIds, userId],
        };
      }),
      chats: state.chats.map((chat) => {
        const readMessagesCount = readMessagesByChatId[chat.id] ?? 0;

        if (readMessagesCount === 0) {
          return chat;
        }

        return {
          ...chat,
          new_messages: Math.max((chat.new_messages ?? 0) - readMessagesCount, 0),
        };
      }),
    };
  }
}
